import { MAIL_LIST_ITEMS_SELECTOR } from './../components/MailListItem';
import { trySetFocusOnElement } from 'dom-utils/lib/utils/focusUtils';
import {
    getAllListItemNodes,
    getCurrentlyFocusableListItemNode,
    getIndexOfListItemNode,
} from 'dom-utils/lib/utils/listUtils';

const KEYS = {
    Left: 'ArrowLeft',
    Right: 'ArrowRight',
    Up: 'ArrowUp',
    Down: 'ArrowDown',
    Space: ' ',
    Tab: 'Tab',
};
const LIST_ITEM_ACTION_BUTTONS_SELECTOR = '[data-type="action"],button';

export function handleKeyPress(
    e: React.KeyboardEvent,
    itemId?: string,
    onSelect?: (itemId: string) => void
): void {
    const allMailItems = getAllListItemNodes(MAIL_LIST_ITEMS_SELECTOR);
    const currentlyFocusedItem = getCurrentlyFocusableListItemNode(MAIL_LIST_ITEMS_SELECTOR);
    const currentNodeIndex = getIndexOfListItemNode(allMailItems, currentlyFocusedItem);

    if (!currentlyFocusedItem) {
        return;
    }

    if (e.key === KEYS.Tab) {
        // When Tab or Shift+Tab keys are pressed, remove 'aria-selected' attribute from currently focused item
        currentlyFocusedItem.setAttribute('aria-selected', 'false');

        const allListItemActionButtons = currentlyFocusedItem.querySelectorAll<HTMLElement>(
            LIST_ITEM_ACTION_BUTTONS_SELECTOR
        );
        // Also, make all list-items triage actions non-focusable
        allListItemActionButtons.forEach(button => {
            if (button) {
                button.tabIndex = -1;
                button.setAttribute('aria-selected', 'false');
            }
        });
        return;
    }

    if ([KEYS.Left, KEYS.Right].includes(e.key)) {
        return handleListItemActionsNavigation(currentlyFocusedItem, e);
    }

    // Select the mail list item on space press
    if (e.key === KEYS.Space) {
        e.preventDefault();
        onSelect?.(itemId);
    }

    // Navigate down the mail list when down arrow is pressed
    if (e.key === KEYS.Down) {
        // No-op when current element is the last element
        if (currentNodeIndex === allMailItems.length - 1) {
            return;
        }
        e.preventDefault();

        const nextItem = allMailItems[currentNodeIndex + 1];
        shiftFocusToTargetElement(nextItem, currentlyFocusedItem);
    }

    // Navigate up the mail list when up arrow is pressed
    if (e.key === KEYS.Up) {
        // No-op when current element is the first element
        if (currentNodeIndex === 0) {
            return;
        }
        e.preventDefault();

        const previousItem = allMailItems[currentNodeIndex - 1];
        shiftFocusToTargetElement(previousItem, currentlyFocusedItem);
    }
}

function shiftFocusToTargetElement(
    targetElement: HTMLElement,
    currentlyFocusedElement: HTMLElement
) {
    // Update target element's tabIndex to 0
    if (targetElement) {
        targetElement.tabIndex = 0;
        targetElement.setAttribute('aria-selected', 'true');
    }

    // Update current element's tabIndex to -1
    if (currentlyFocusedElement) {
        currentlyFocusedElement.tabIndex = -1;
        currentlyFocusedElement.setAttribute('aria-selected', 'false');
    }

    // Focus on the target element
    trySetFocusOnElement(targetElement);
}

function handleListItemActionsNavigation(listItemElement: HTMLElement, e: React.KeyboardEvent) {
    const allListItemActionButtons = listItemElement.querySelectorAll<HTMLElement>(
        LIST_ITEM_ACTION_BUTTONS_SELECTOR
    );
    const currentlyFocusedButton = listItemElement.querySelector<HTMLElement>(
        '[aria-selected="true"]'
    );
    const currentButtonIndex = getIndexOfListItemNode(
        allListItemActionButtons,
        currentlyFocusedButton
    );

    if (e.key === KEYS.Left) {
        // No-op when current button is the first button
        if (currentButtonIndex < 1) {
            return;
        }
        e.preventDefault();

        const previousButton = allListItemActionButtons[currentButtonIndex - 1];
        shiftFocusToTargetElement(previousButton, currentlyFocusedButton);
    }

    if (e.key === KEYS.Right) {
        // No-op when current button is the last button
        if (currentButtonIndex === allListItemActionButtons.length - 1) {
            return;
        }
        e.preventDefault();

        const nextButton = allListItemActionButtons[currentButtonIndex + 1];
        shiftFocusToTargetElement(nextButton, currentlyFocusedButton);
    }
}
